import { gql } from '@apollo/client';
import { postWords } from './postWords';
import type { ItemCheck, VoiceCheck } from './voiceWords';

/** Reach (Calendar · Campaigns · Channels): queries, channel names and dates. */

export const REACH_POST_FIELDS = `
  id
  platform
  summary
  title
  imageUrl
  postUrl
  postType
  channelSettings
  mediaAssetId
  waitsForMedia
  projectId
  projectPostId
  createdVia
  reachStatus
  failure
  reviewStatus
  reviewerName
  scheduleTime
  proposedTime
  socialPublishDate
  livePostUrl
  deletionStatus
  socialProfileId
  updatedAt
  offSiteDomain
  voiceCheck
  itemCheck
`;

export const REACH_CALENDAR = gql`
  query ReachCalendar($accountId: ID!, $from: String!, $to: String!) {
    reachCalendar(accountId: $accountId, from: $from, to: $to) {
      posts { ${REACH_POST_FIELDS} work }
      waiting { ${REACH_POST_FIELDS} work }
      drafts { ${REACH_POST_FIELDS} }
    }
  }
`;

/**
 * Social › Posts (backlog 4e7c78c8; apiserver reach/services/postList.js):
 * every post, filtered, 50 a page. counts: { status: { all, waiting, … },
 * channel: { all, [socialProfileId]: n }, source: { all, automatic, you, agent,
 * [campaignId]: n } }; sources: the dated campaigns.
 */
export const REACH_POSTS = gql`
  query ReachPosts(
    $accountId: ID!
    $channel: ID
    $status: String
    $source: String
    $q: String
    $sort: String
    $cursor: String
  ) {
    reachPosts(
      accountId: $accountId
      channel: $channel
      status: $status
      source: $source
      q: $q
      sort: $sort
      cursor: $cursor
    ) {
      posts { ${REACH_POST_FIELDS} listSource campaignName linkTitle channelHandle }
      total
      nextCursor
      counts
      sources
    }
  }
`;

/** Calendar's "New on your site, not shared yet" (apiserver reach/services/unshared.js). */
export const REACH_UNSHARED = gql`
  query ReachUnshared($accountId: ID!) {
    reachUnshared(accountId: $accountId) {
      articles {
        id
        title
        link
        publishedAt
      }
      # Live pages not shared yet, while the site has posted nothing (Prototype v15).
      pages {
        id
        title
        path
        link
      }
      everPosted
      channels {
        id
        platform
        name
      }
      sharingPaused
    }
  }
`;

/** One waiting post per new article per channel: nothing posts until approved. */
export const DRAFT_POSTS_FOR_NEW_ARTICLES = gql`
  mutation DraftPostsForNewArticles($accountId: ID!) {
    draftPostsForNewArticles(accountId: $accountId) {
      drafted
      skipped
      articles
    }
  }
`;

/** "X and Threads", "X, Threads and LinkedIn". */
export function andList(items: string[]): string {
  return items.length < 2
    ? items.join('')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** A draft goes on the calendar: "now" (in a minute) or an ISO time ahead. */
export const SCHEDULE_POST_DRAFT = gql`
  mutation ScheduleSocialPostDraft($id: ID!, $scheduleTime: String!) {
    scheduleSocialPostDraft(id: $id, scheduleTime: $scheduleTime) { ${REACH_POST_FIELDS} }
  }
`;

/** A draft finished in Write a post: it stays a draft (SCHEDULE_POST_DRAFT gives it a time). */
export const UPDATE_POST_DRAFT = gql`
  mutation UpdateSocialPostDraft($id: ID!, $input: UpdatePostDraftInput!) {
    updateSocialPostDraft(id: $id, input: $input) { ${REACH_POST_FIELDS} }
  }
`;

export const DISCARD_POST_DRAFT = gql`
  mutation DiscardSocialPostDraft($id: ID!) {
    discardSocialPostDraft(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_SOCIAL_POST = gql`
  mutation ApproveSocialPost($id: ID!, $reason: String) {
    approveSocialPost(id: $id, reason: $reason) { ${REACH_POST_FIELDS} }
  }
`;

/**
 * The approval page (/reach/approve/:postId): one post with its channel and
 * site, and the person's note when they sent it back. Null when the post isn't
 * on a site the person belongs to.
 */
export const REACH_POST_FOR_APPROVAL = gql`
  query ReachPostForApproval($id: ID!) {
    socialPost(id: $id) {
      ${REACH_POST_FIELDS}
      accountId
      reviewNote
      reviewedAt
      account { id name }
      socialProfile {
        id
        platform
        profileId
        profileName
        profileUsername
        profileLink
        isActive
        isPublishing
        approvalMode
      }
    }
  }
`;

/** Send a waiting post back to its agent as a draft, with what should change. */
export const REQUEST_SOCIAL_POST_CHANGES = gql`
  mutation RequestSocialPostChanges($id: ID!, $note: String!) {
    requestSocialPostChanges(id: $id, note: $note) { ${REACH_POST_FIELDS} reviewNote reviewedAt }
  }
`;

/** A post that didn't go out goes out again in a minute ("Try again"). */
/**
 * Reach panel → trash (UX optimization review, 2026-10-24): a scheduled,
 * waiting or failed post leaves the calendar and its campaign and won't go out.
 * Undo puts it back, for 10 minutes (apiserver review.js UNDO_DELETE_MS).
 */
export const DELETE_SOCIAL_POST = gql`
  mutation DeleteSocialPost($id: ID!) {
    deleteSocialPost(id: $id) {
      id
    }
  }
`;
/** Deleting or Deleted for a post that's been deleted; null for the rest. */
export const goneStatus = (p: Pick<ReachPost, 'deletionStatus'>) =>
  p.deletionStatus === 'requested' ? 'deleting' : p.deletionStatus ? 'deleted' : null;

export const PERMANENTLY_DELETE_SOCIAL_POST = gql`
  mutation PermanentlyDeleteSocialPost($id: ID!) {
    permanentlyDeleteSocialPost(id: $id) {
      id
    }
  }
`;
export const UNDO_DELETE_SOCIAL_POST = gql`
  mutation UndoDeleteSocialPost($id: ID!) {
    undoDeleteSocialPost(id: $id) {
      id
    }
  }
`;

export const RETRY_SOCIAL_POST = gql`
  mutation RetrySocialPost($id: ID!) {
    retrySocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

/** Undo of Skip / Try again on a missed post: missed again at its old time. */
export const RESTORE_MISSED_SOCIAL_POST = gql`
  mutation RestoreMissedSocialPost($id: ID!, $scheduleTime: String!, $reviewStatus: String) {
    restoreMissedSocialPost(id: $id, scheduleTime: $scheduleTime, reviewStatus: $reviewStatus) { ${REACH_POST_FIELDS} }
  }
`;

export const SKIP_SOCIAL_POST = gql`
  mutation SkipSocialPost($id: ID!) {
    skipSocialPost(id: $id) { ${REACH_POST_FIELDS} }
  }
`;

export const APPROVE_CAMPAIGN = gql`
  mutation ApproveCampaign($id: ID!, $reason: String) {
    approveCampaign(id: $id, reason: $reason) {
      approved
      scheduled
      undated
      skippedChannels
      held
      heldKeys
    }
  }
`;

export const REACH_CHANNELS = gql`
  query ReachChannels($accountId: ID!) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
      canDeletePosts
    }
  }
`;

/** Reach → Channels: the channels with each one's last post that has an outcome. */
export const REACH_CHANNEL_ROWS = gql`
  query ReachChannelRows($accountId: ID!, $since: String) {
    socialProfiles(accountId: $accountId) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
      lastPost { ${REACH_POST_FIELDS} }
      missedPosts(since: $since) {
        count
        older
        latest { ${REACH_POST_FIELDS} }
        posts { ${REACH_POST_FIELDS} }
      }
      stats {
        followers
        change30d
        asOf
      }
      metricsNeedReconnect
      updatedAt
    }
  }
`;

/** Connect the channels picked from a sign-in (the ticket came from the API's exchange). */
export const CONNECT_SOCIAL_CHANNELS = gql`
  mutation ConnectSocialChannels($input: ConnectSocialChannelsInput!) {
    connectSocialChannels(input: $input) {
      id
      platform
      profileId
      profileName
      profileUsername
      profileLink
      isActive
      isPublishing
      approvalMode
    }
  }
`;

/**
 * Reach → Channels "Disconnect": the stored credentials are removed; the row
 * stays, inactive, so its posts keep their channel. (Settings → Social's
 * "Active" switch is a pause through UPDATE_SOCIAL_PROFILE, credentials kept.)
 */
export const DISCONNECT_SOCIAL_CHANNEL = gql`
  mutation DisconnectSocialChannel($id: ID!) {
    disconnectSocialChannel(id: $id) {
      id
      isActive
      isPublishing
    }
  }
`;

/** A person's own post (Reach → Calendar → Write a post): one call per channel. */
/** What each network takes (apiserver reach/services/postFormats.js). */
export const SOCIAL_NETWORK_RULES = gql`
  query SocialNetworkRules {
    socialNetworkRules
  }
`;

export const CREATE_REACH_POST = gql`
  mutation CreateReachPost($input: CreateSocialPostInput!) {
    createSocialPost(input: $input) { ${REACH_POST_FIELDS} }
  }
`;

export const SET_APPROVAL_MODE = gql`
  mutation SetSocialProfileApprovalMode($id: ID!, $mode: String!) {
    setSocialProfileApprovalMode(id: $id, mode: $mode) {
      id
      approvalMode
    }
  }
`;

/** Social → Channels' Publishing switch, the master off for a channel. */
export const SET_CHANNEL_PUBLISHING = gql`
  mutation SetSocialProfilePublishing($id: ID!, $on: Boolean!) {
    setSocialProfilePublishing(id: $id, on: $on) {
      id
      isPublishing
    }
  }
`;

/** Publishing is off: it shows in a picker, grayed out. A server without the flag counts as on. */
export const publishingOff = (c: { isPublishing?: boolean | null }) => c.isPublishing === false;

/** The line a picker shows on a channel with publishing off. */
export const PUBLISHING_OFF_WORDS = 'Publishing is off. Turn it on in Channels.';

export interface ReachPost {
  id: string;
  platform: string;
  summary?: string | null;
  title?: string | null;
  /** The post's photo, when it has one (Today shows it small beside the caption). */
  imageUrl?: string | null;
  /** The link the post carries. */
  postUrl?: string | null;
  /** Where the post lives on its network, once it's out. */
  livePostUrl?: string | null;
  /** requested: on its way down from its network. deleted: in Social › Posts › Deleted. */
  deletionStatus?: string | null;
  /** photo | video | reel | story | link | text. */
  postType?: string | null;
  /** { tiktok: { privacy, allowComments, allowDuet, allowStitch }, media, mediaError } */
  channelSettings?: {
    tiktok?: {
      privacy?: string;
      allowComments?: boolean;
      allowDuet?: boolean;
      allowStitch?: boolean;
    };
    media?: { width?: number | null; height?: number | null; durationSeconds?: number | null };
    mediaError?: string;
  } | null;
  /** The library video or photo it uses. */
  mediaAssetId?: string | null;
  /** "Post when ready": held until the video's MP4 exists. */
  waitsForMedia?: boolean | null;
  /** The campaign it belongs to (always-on, or filed under a dated one). */
  projectId?: string | null;
  /** Set when the post is one of a campaign plan's posts (it changes in the campaign). */
  projectPostId?: string | null;
  /** app | mcp | api | campaign */
  createdVia?: string | null;
  reachStatus: string;
  /** Why a missed post didn't go out, a clause in plain words (missed posts only). */
  failure?: string | null;
  reviewStatus?: string | null;
  /** Who approved it or sent it again; the calendar's rows carry it. */
  reviewerName?: string | null;
  scheduleTime?: string | null;
  proposedTime?: string | null;
  socialPublishDate?: string | null;
  socialProfileId: string;
  /** Last saved (Drafts: "Saved Oct 8"). */
  updatedAt?: string | null;
  /** "nasa.gov" when the post's link leaves the site (UX review round 9), else null. */
  offSiteDomain?: string | null;
  /**
   * The calendar card's work strip (campaign board): notes, due date (null:
   * 2 days before it goes out) and done. REACH_CALENDAR only; null once it
   * went out or was skipped.
   */
  work?: { note: string; dueDate: string | null; done: boolean } | null;
  /** The voice and style check (voiceWords.ts). Null once it's out or skipped. */
  voiceCheck?: VoiceCheck | null;
  /** Item checks before it goes out (voiceWords.ts). Null once it's out or skipped. */
  itemCheck?: ItemCheck | null;
}

/** An always-on campaign's conditions (apiserver reach/services/alwaysOn.js). */
export interface AlwaysOnConditions {
  priority?: number;
  postType?: 'auto' | 'link' | 'text' | 'photo' | 'video' | 'random' | string;
  maxAgeHours?: number;
  delayHours?: number;
  immediate?: boolean;
  schedule?: Array<{ time?: string; minuteMark?: number; intervalMinutes?: number }>;
  windows?: Array<{
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    spansMidnight?: boolean;
  }>;
  include?: string[];
  exclude?: string[];
  mediaTypes?: string[];
  /**
   * Only on rules migrated from the old screen: their older filter in words
   * (the server never sends the SQL; conditionsForApp). Read-only; removable.
   */
  legacyFilter?: import('./alwaysOnSentence').LegacyFilter;
}

export interface Channel {
  id: string;
  platform: string;
  profileId?: string | null;
  profileName?: string | null;
  profileUsername?: string | null;
  profileLink?: string | null;
  isActive: boolean;
  isPublishing?: boolean | null;
  approvalMode: 'ask' | 'auto' | string;
  /** An Instagram account whose live posts can be deleted from here (REACH_CHANNELS only). */
  canDeletePosts?: boolean;
  /** The last post with an outcome (REACH_CHANNEL_ROWS only). */
  lastPost?: ReachPost | null;
  /**
   * Posts that didn't go out and wait for a decision — the same window Today
   * lists them in (since passed to the query) — and how many older ones (up to
   * 30 days) went unposted with nobody asked (REACH_CHANNEL_ROWS only).
   */
  missedPosts?: {
    count: number;
    older?: number;
    latest?: ReachPost | null;
    posts?: ReachPost[];
  } | null;
  /** The engine's daily follower snapshot (REACH_CHANNEL_ROWS only); null when none. */
  stats?: { followers: number; change30d?: number | null; asOf: string } | null;
  /**
   * Its posts' views need a new sign-in: an Instagram account connected through
   * a Facebook Page without instagram_manage_insights (REACH_CHANNEL_ROWS only).
   */
  metricsNeedReconnect?: boolean;
  /** Last change to the row; for a disconnected channel, when it was disconnected. */
  updatedAt?: string | null;
}

/**
 * Followers and 30 days for a channel row (Prototype v14): "1,520" and "+120",
 * "—" where the platform gives no count, nothing has been collected yet, or
 * there is no snapshot from 30 days back. A rise is green, as the design shows.
 */
export function channelStatCells(stats: Channel['stats']): {
  followers: string;
  growth: string;
  growthTone: 'up' | 'flat' | 'down' | 'none';
} {
  if (!stats) return { followers: '—', growth: '—', growthTone: 'none' };
  const change = stats.change30d;
  return {
    followers: stats.followers.toLocaleString('en-US'),
    growth:
      change == null
        ? '—'
        : change > 0
          ? `+${change.toLocaleString('en-US')}`
          : change < 0
            ? `−${Math.abs(change).toLocaleString('en-US')}`
            : '0',
    growthTone: change == null ? 'none' : change > 0 ? 'up' : change < 0 ? 'down' : 'flat',
  };
}

/**
 * A channel's health (Prototype v16): "3 posts waiting for a decision · 29
 * older, not posted" — the same posts, in the same window, as Today's "3
 * Facebook posts missed their time" (UX review round 9 found 32 here against
 * 3 there). Review opens that list. "Last post didn't go out · Oct 17" from an
 * older server. Null when none waits.
 */
export function lastPostProblem(
  c: Pick<Channel, 'lastPost' | 'missedPosts'>,
  now = new Date()
): string | null {
  const missed = c.missedPosts;
  if (missed && missed.count > 0) {
    const older = missed.older || 0;
    return (
      `${missed.count} ${missed.count === 1 ? 'post' : 'posts'} waiting for a decision` +
      (older ? ` · ${older} older, not posted` : '')
    );
  }
  // An older server without missedPosts: the last post's own outcome.
  if (!missed && c.lastPost?.reachStatus === 'missed') {
    const at = shortDate(postTime(c.lastPost), now);
    return at ? `Last post didn’t go out · ${at}` : 'Last post didn’t go out';
  }
  return null;
}

/**
 * The channel's sign-in has run out (UX review round 20): its newest post with
 * an outcome didn't go out because the connection needs renewing (the server's
 * plain-words reason, reach/services/review.js), and nothing has gone out since.
 * Every post on it fails until someone reconnects.
 */
export function connectionExpired(c: Pick<Channel, 'lastPost' | 'missedPosts'>): boolean {
  const renew = (p?: ReachPost | null): p is ReachPost => needsReconnect(p);
  if (renew(c.lastPost ?? c.missedPosts?.latest)) return true;
  // The last post can be an older one, or a later post that simply wasn't sent
  // (QA round 23: X counted "1 post waiting" that needed renewing, no note).
  // Any waiting post that ran into it decides, unless one went out since.
  const stuck = [c.missedPosts?.latest, ...(c.missedPosts?.posts || [])].find(renew);
  if (!stuck) return false;
  const last = c.lastPost;
  const wentOut = last && (last.reachStatus === 'posted' || last.reachStatus === 'publishing');
  return !(wentOut && (postTime(last)?.getTime() ?? 0) >= (postTime(stuck)?.getTime() ?? 0));
}

/** A post that didn't go out because its channel's sign-in ran out: Try again fails until Reconnect. */
export const needsReconnect = (p?: Pick<ReachPost, 'reachStatus' | 'failure'> | null) =>
  p?.reachStatus === 'missed' && /needs to be renewed/i.test(p.failure || '');

/**
 * A channel's posts waiting for approval (QA round 23: Calendar and Today had
 * a LinkedIn post to approve, its Channels row said nothing). The same list
 * Today and the Calendar's "things need you" read (reachCalendar.waiting).
 */
export function waitingForApproval(
  c: Pick<Channel, 'id' | 'platform' | 'profileId'>,
  all: Array<Pick<Channel, 'id' | 'platform' | 'profileId'>>,
  waiting: ReachPost[]
): ReachPost[] {
  // An older row for the same account (a reconnect) counts, as missedPosts does.
  const ids = new Set([
    c.id,
    ...all
      .filter(
        (o) =>
          c.profileId &&
          o.profileId === c.profileId &&
          o.platform.toLowerCase() === c.platform.toLowerCase()
      )
      .map((o) => o.id),
  ]);
  return waiting.filter((p) => p.socialProfileId && ids.has(p.socialProfileId));
}

/** "1 post waiting for your approval", or null. */
export function approvalWords(n: number): string | null {
  return n ? `${n} ${n === 1 ? 'post' : 'posts'} waiting for your approval` : null;
}

/** "Connection expired · nothing can post until you reconnect. 2 posts are waiting." */
export function expiredWords(waiting: number): string {
  return (
    'Connection expired · nothing can post until you reconnect.' +
    (waiting ? ` ${waiting} ${waiting === 1 ? 'post is' : 'posts are'} waiting.` : '')
  );
}

/** The post Fix opens: the latest one that didn't go out. */
export const postToFix = (c: Pick<Channel, 'lastPost' | 'missedPosts'>): ReachPost | null =>
  c.missedPosts?.latest || (c.lastPost?.reachStatus === 'missed' ? c.lastPost : null);

/** Platforms we can post to, in the order Add a channel shows them. */
export const POSTABLE = [
  'x',
  'threads',
  'instagram',
  'facebook',
  'linkedin',
  'bluesky',
  'youtube',
  'tiktok',
] as const;

const NAMES: Record<string, [string, string]> = {
  x: ['X', 'X'],
  twitter: ['X', 'X'],
  threads: ['Threads', '@'],
  instagram: ['Instagram', 'IG'],
  facebook: ['Facebook', 'f'],
  youtube: ['YouTube', 'YT'],
  tiktok: ['TikTok', 'TT'],
  linkedin: ['LinkedIn', 'in'],
  snapchat: ['Snapchat', 'SC'],
  bluesky: ['Bluesky', 'B'],
};
export const channelName = (p: string) => NAMES[p?.toLowerCase()]?.[0] || p;
/**
 * What kind of account a channel posts as (Prototype v13/v14): a Facebook Page,
 * a LinkedIn member's own profile, a YouTube channel, otherwise an account. A
 * LinkedIn channel is a company Page when it posts as an organization (its
 * profileId is the author URN, urn:li:organization:… vs urn:li:person:…);
 * without the profileId it reads as a personal profile, the safer warning.
 */
const KINDS: Record<string, string> = {
  facebook: 'Page',
  linkedin: 'Personal profile',
  youtube: 'Channel',
};
export const channelKind = (p: string, profileId?: string | null) =>
  p?.toLowerCase() === 'linkedin' && profileId?.startsWith('urn:li:organization:')
    ? 'Page'
    : KINDS[p?.toLowerCase()] || 'Account';
export const isPersonalProfile = (p: string, profileId?: string | null) =>
  channelKind(p, profileId) === 'Personal profile';

/** "View Page", "View channel", otherwise "View profile". */
export const viewChannelLabel = (p: string, profileId?: string | null) => {
  const kind = channelKind(p, profileId);
  return kind === 'Page' ? 'View Page' : kind === 'Channel' ? 'View channel' : 'View profile';
};

/**
 * A stored link to open on the network, only when it's a web address: an older
 * channel's link could be typed in by hand, and an href runs whatever it's given.
 */
export const networkLink = (url?: string | null) => {
  const u = url?.trim() || '';
  return /^https?:\/\//i.test(u) ? u : null;
};

type NamedChannel = Pick<
  Channel,
  'id' | 'platform' | 'profileId' | 'profileName' | 'profileUsername'
>;

/**
 * A channel as campaigns and drafts name it (Prototype chName): the network,
 * plus whose it is for a person's own profile or when another channel in
 * `among` is on the same network — "X", "LinkedIn (Hai Tran)". Two channels
 * named after the same person otherwise read the same (QA 2026-10-21).
 */
export function channelLabel(c: NamedChannel, among: NamedChannel[] = []): string {
  const net = channelName(c.platform);
  const who = c.profileName || (c.profileUsername ? `@${c.profileUsername.replace(/^@/, '')}` : '');
  const twin = among.some(
    (o) => o.id !== c.id && o.platform?.toLowerCase() === c.platform?.toLowerCase()
  );
  return who && (isPersonalProfile(c.platform, c.profileId) || twin) ? `${net} (${who})` : net;
}

/** Whose a channel is: "@htblueline", "Space Interests". */
export function channelWho(c: Pick<NamedChannel, 'profileName' | 'profileUsername'>) {
  const handle = c.profileUsername?.trim();
  return handle && !/\s/.test(handle)
    ? `@${handle.replace(/^@/, '')}`
    : c.profileName || handle || '';
}

/**
 * A campaign's channel by its account: "Facebook · Space Interests", "X ·
 * @htblueline". A site can have two connections on one network (Round 44 §4).
 */
export function channelAccount(
  c: Pick<NamedChannel, 'platform' | 'profileName' | 'profileUsername'>
) {
  const who = channelWho(c);
  return who ? `${channelName(c.platform)} · ${who}` : channelName(c.platform);
}

/**
 * A sharing rule's channels that were disconnected (QA round 23: rules read
 * "● On" for Threads and Instagram after both were disconnected). The rule
 * keeps them, but nothing posts there until they're connected again; with
 * none left connected, the rule posts nothing at all (alwaysOn.js skips
 * inactive channels).
 */
export function disconnectedRuleChannels<C extends { isActive?: boolean | null }>(
  channels: C[]
): { off: C[]; stalled: boolean } {
  const off = channels.filter((c) => c.isActive === false);
  return { off, stalled: off.length > 0 && off.length === channels.length };
}

/** "America/Los_Angeles (PDT)": the zone a picked time is read in. */
/** "New York": the city of the browser's time zone, for "Your time · New York". */
export function zoneCity(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  return zone.split('/').pop()?.replace(/_/g, ' ') || localZone();
}

export function localZone(now = new Date()): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const short =
    new Intl.DateTimeFormat([], { timeZoneName: 'short' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value || '';
  return [zone.replace(/_/g, ' '), short && `(${short})`].filter(Boolean).join(' ');
}

export const channelMark = (p: string) =>
  NAMES[p?.toLowerCase()]?.[1] || p?.[0]?.toUpperCase() || '?';

/** Dates arrive as ISO strings or epoch milliseconds (as strings). */
export function toDate(v?: string | null): Date | null {
  if (!v) return null;
  const d = /^\d{10,}$/.test(v) ? new Date(Number(v)) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Jan 13", or "Jan 13, 2025" when it isn't this year. */
/**
 * A campaign's end date (projects.deadline, a DATE) as that calendar day here.
 * The API sends a DATE as UTC midnight ("2026-10-31T00:00:00.000Z"), which in
 * Los Angeles is the evening before — read its UTC day, never the local one.
 */
export function calendarDate(v?: string | null): Date | null {
  const d = toDate(v);
  return d ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) : null;
}

/**
 * The calendar day `n` days after `d`, at local midnight. Steps by date, never
 * by 24 hours: on the day the clocks change a day is 23 or 25 hours long, so
 * adding 86,400,000 ms to a midnight lands on the same day again or skips one
 * (Reach › Calendar showed "Sun 1" twice and no Mon Nov 2).
 */
export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** A picked day as the DATE the API stores ("2026-10-31"), never shifted by a zone. */
export function dateValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A campaign brief's opening as one plain line for a card: markup, markdown
 * and the agent's all-caps section labels ("STRATEGY OVERVIEW", "Solution
 * Description:") dropped.
 */
export function briefExcerpt(raw?: string | null, max = 160): string {
  const text = String(raw || '')
    .replace(/<[^>]+>/g, ' ')
    .split(/\r?\n/)
    .map((l) =>
      l
        .replace(/^[#>*\-\s]+/, '')
        .replace(/[*_`]+/g, '')
        .trim()
    )
    .filter((l) => l && !/^[A-Z0-9 &/,'’-]{3,}:?$/.test(l))
    .map((l) => l.replace(/^[A-Z][\w ’'-]{0,40}:\s*/, ''))
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
}

export function shortDate(v?: string | Date | null, now = new Date()): string {
  const d = v instanceof Date ? v : toDate(v);
  if (!d) return '';
  return d.toLocaleDateString(
    [],
    d.getFullYear() === now.getFullYear()
      ? { month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

/**
 * A dated campaign's run, from the campaign itself (never from its posts):
 * start → deadline, the same range on Reach → Campaigns and on Today. The
 * start is the campaign's own start date, else the day it was created.
 * The year shows when it isn't this year: "Jan 13 – Jan 18, 2025" (one year),
 * "Dec 20, 2025 – Jan 4" (across years).
 */
/**
 * The day a campaign starts: its own start date (a DATE, read as that calendar
 * day), else the day it was created (a moment, read in local time). Reading
 * the creation time as a DATE gave its UTC day, a day later in the evening
 * here, so the list said Oct 19, the detail and Edit said Oct 20, and saving
 * Edit wrote Oct 20 (QA round 12). Every view uses this.
 */
export function campaignStart(c: { createdAt?: string | null; startDate?: string | null }) {
  return calendarDate(c.startDate) || toDate(c.createdAt);
}

export function campaignRange(
  c: { createdAt?: string | null; startDate?: string | null; deadline?: string | null },
  now = new Date()
): string {
  // Its own start date (Edit campaign → Starts), else the day it was created.
  const start = campaignStart(c);
  const end = calendarDate(c.deadline);
  if (start && end && start.getFullYear() === end.getFullYear()) {
    const day = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${day(start)} – ${shortDate(end, now)}`;
  }
  if (start && end) return `${shortDate(start, now)} – ${shortDate(end, now)}`;
  return shortDate(start || end, now);
}

/** When the calendar places a post: when it went out, will go out, or is proposed to. */
export const postTime = (p: ReachPost) =>
  toDate(p.socialPublishDate) || toDate(p.scheduleTime) || toDate(p.proposedTime);

/** A post's words as readers see them, without what the feed sent around them. */
export const postText = (p: ReachPost) => postWords(p);

export const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const dayTime = (d: Date) =>
  d.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Calendar → post → "Links to": what the post's link opens on the site, by name. */
export const REACH_POST_LINK = gql`
  query ReachPostLink($postId: ID!) {
    reachPostLink(postId: $postId) {
      kind
      id
      title
      url
      offSite
      ownUrl
    }
  }
`;

/** "Use your page instead" (Prototype v16): the post links to the same article on the site. */
export const LINK_SOCIAL_POST_TO_SITE = gql`
  mutation LinkSocialPostToSite($id: ID!) {
    linkSocialPostToSite(id: $id) {
      ${REACH_POST_FIELDS}
    }
  }
`;

/** The site's address ("kt.tinycms.ai"), for the composer's off-site link warning. */
export const REACH_SITE_HOST = gql`
  query ReachSiteHost($accountId: ID!) {
    reachSiteHost(accountId: $accountId)
  }
`;

/** A page or article a post can link to (Write a post → Link picker). */
export interface LinkTarget {
  kind: 'article' | 'page';
  id: string;
  title: string;
  url: string;
  /** An article's main image or a page's share image. */
  imageUrl?: string | null;
  category?: string | null;
  publishedDate?: string | null;
}

/** Live pages and published articles matching q (blank: pages, then the latest articles). */
export const REACH_LINK_TARGETS = gql`
  query ReachLinkTargets($accountId: ID!, $q: String) {
    reachLinkTargets(accountId: $accountId, q: $q) {
      kind
      id
      title
      url
      imageUrl
      category
      publishedDate
    }
  }
`;

/** Article screen → Reach: the posts that link to the article, and the link a new one uses. */
export const REACH_ARTICLE_POSTS = gql`
  query ReachArticlePosts($accountId: ID!, $articleId: ID!) {
    reachArticlePosts(accountId: $accountId, articleId: $articleId) {
      link
      posts { ${REACH_POST_FIELDS} }
    }
  }
`;

/**
 * The Calendar with one post open (the article screen's post rows): the week
 * that holds it, and the post. A draft has no time, so its week is this one.
 */
export const calendarPostHref = (p: ReachPost) => {
  const at = postTime(p);
  return `/reach/calendar?post=${encodeURIComponent(p.id)}${at ? `&at=${encodeURIComponent(at.toISOString())}` : ''}`;
};

/**
 * "nasa.gov" when a link leaves the site (host "kt.tinycms.ai"), else null —
 * the composer's check as you type; posts carry the server's offSiteDomain.
 */
export function offSiteOf(url: string, host: string | null | undefined): string | null {
  if (!url.trim() || !host) return null;
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`);
  } catch {
    return null;
  }
  const bare = (h: string) => h.toLowerCase().replace(/^www\./, '');
  return bare(parsed.host) !== bare(host) ? bare(parsed.host) : null;
}

/** Calendar → post → Edit: a scheduled, waiting or missed post's words, link and image. */
export const UPDATE_PENDING_POST = gql`
  mutation UpdatePendingSocialPost($id: ID!, $input: UpdatePostDraftInput!) {
    updatePendingSocialPost(id: $id, input: $input) {
      ${REACH_POST_FIELDS}
    }
  }
`;

/** The site's campaigns by id, for a post's "Campaign" row. */
export const REACH_CAMPAIGN_NAMES = gql`
  query ReachCampaignNames($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
    }
  }
`;

/**
 * A campaign waiting on a yes, as a row of Reach's "N things need you" (UX
 * review round 12): its name, then what it holds and when it runs — "3 posts
 * waiting · Oct 15 – Nov 1" — the same campaigns, in the same words, as Today.
 */
export function campaignDecision(
  c: {
    kind: string;
    name: string;
    createdAt?: string | null;
    startDate?: string | null;
    deadline?: string | null;
    posts: Array<{ status: string }>;
  },
  now = new Date()
): { title: string; meta: string; ended: boolean } {
  if (c.kind === 'always_on') {
    return {
      title: c.name,
      meta: 'Always on · your agent set this up. Nothing posts until you turn it on.',
      ended: false,
    };
  }
  const end = calendarDate(c.deadline);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (end && end < today) {
    return {
      title: c.name,
      meta: `Ended ${shortDate(end, now)} · never approved. Nothing went out.`,
      ended: true,
    };
  }
  const n = c.posts.filter((p) => p.status === 'in_review').length;
  const range = campaignRange(c, now);
  return {
    title: c.name,
    meta: `${n} ${n === 1 ? 'post' : 'posts'} waiting${range ? ` · ${range}` : ''}`,
    ended: false,
  };
}
